/**
 * Balance de Comprobación - Finance 360 (S2-02)
 *
 * La API existía (`/contabilidad/estados/comprobacion`) y no tenía pantalla.
 * Con un período o un rango separa el saldo anterior de los movimientos:
 * saldo anterior + débitos − créditos = saldo final, por cuenta. Con "Todo el
 * tiempo" es el acumulado a hoy, como lo daba la API.
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { LoadingPage } from '../../components/LoadingSpinner';
import MonthRangePicker from '../../components/MonthRangePicker';
import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
import usePeriodosContables from '../../hooks/usePeriodosContables';
import { periodoQueCubre, valorDePeriodo, hoyCR } from '../../utils/periodosContables';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BalanceComprobacion() {
  const { currentEmisor } = useAuthStore();
  const periodos = usePeriodosContables();
  const [filtro, setFiltro] = useFiltroPeriodo('BalanceComprobacion', null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sin filtro guardado, el período contable de hoy apenas carguen los períodos.
  useEffect(() => {
    if (filtro || periodos.length === 0) return;
    const actual = periodoQueCubre(periodos, hoyCR());
    setFiltro(actual ? valorDePeriodo(actual) : { modo: 'rango', desde: '', hasta: '' });
  }, [filtro, periodos, setFiltro]);

  useEffect(() => {
    if (!currentEmisor || !filtro) return;
    setLoading(true);
    setError(null);
    contabilidadApi.getBalanceComprobacion({ fecha: filtro.hasta || undefined, fechaDesde: filtro.desde || undefined })
      .then(r => setBalance(r.data?.data || null))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [currentEmisor, filtro]);

  if (!filtro || (loading && !balance)) return <LoadingPage />;
  const t = balance?.totales || {};

  return (
    <div className="space-y-4" data-prueba="balance-comprobacion">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Balance de comprobación</h1>
          <p className="text-sm text-gray-500">
            {balance?.desde ? `Del ${balance.desde} al ${balance.fecha}` : `Acumulado al ${balance?.fecha || ''}`}
          </p>
        </div>
        <MonthRangePicker value={filtro} onChange={setFiltro} periodos={periodos} />
      </div>

      {error && <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {balance && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${balance.cuadra ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
          data-prueba="comprobacion-cuadra">
          {balance.cuadra ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          {balance.cuadra
            ? 'Los débitos y los créditos del rango cuadran.'
            : `Los débitos y los créditos no cuadran: diferencia ${fmt(t.debitos - t.creditos)}.`}
        </div>
      )}

      {balance && (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full text-sm" data-prueba="tabla-comprobacion">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Código</th>
                <th className="px-3 py-2 text-left">Cuenta</th>
                {balance.desde && <th className="px-3 py-2 text-right">Saldo anterior</th>}
                <th className="px-3 py-2 text-right">Débitos</th>
                <th className="px-3 py-2 text-right">Créditos</th>
                <th className="px-3 py-2 text-right">Saldo deudor</th>
                <th className="px-3 py-2 text-right">Saldo acreedor</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {balance.cuentas.map(c => (
                <tr key={c.codigo} className="hover:bg-gray-50">
                  <td className="px-3 py-1.5 font-mono text-gray-600">{c.codigo}</td>
                  <td className="px-3 py-1.5">{c.nombre}</td>
                  {balance.desde && <td className="px-3 py-1.5 text-right">{fmt(c.saldoAnterior)}</td>}
                  <td className="px-3 py-1.5 text-right">{fmt(c.debitos)}</td>
                  <td className="px-3 py-1.5 text-right">{fmt(c.creditos)}</td>
                  <td className="px-3 py-1.5 text-right">{c.saldoDeudor ? fmt(c.saldoDeudor) : ''}</td>
                  <td className="px-3 py-1.5 text-right">{c.saldoAcreedor ? fmt(c.saldoAcreedor) : ''}</td>
                </tr>
              ))}
              {balance.cuentas.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">Sin movimientos en el rango.</td></tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="px-3 py-2" colSpan={balance.desde ? 3 : 2}>Totales</td>
                <td className="px-3 py-2 text-right">{fmt(t.debitos)}</td>
                <td className="px-3 py-2 text-right">{fmt(t.creditos)}</td>
                <td className="px-3 py-2 text-right">{fmt(t.saldosDeudores)}</td>
                <td className="px-3 py-2 text-right">{fmt(t.saldosAcreedores)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
